import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TagInput } from '../components/TagInput';
import type { CmsMediaRecord } from '../hooks/useCmsMedia';

const SUPPORTED_LOCALES = ['en', 'it', 'es', 'fr', 'de'] as const;
const LOCALE_LABELS: Record<string, string> = { en: 'EN', it: 'IT', es: 'ES', fr: 'FR', de: 'DE' };

// ─── Usage badge colors (matches ExtraMediaBrowser) ──────────────────────────
function usageBadgeClass(usage: string): string {
  switch (usage) {
    case 'product-main': return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 'product-secondary': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    case 'product-third': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    case 'product-extra': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white/60';
  }
}

// Usage values that mean "this photo is actively shown on the website"
const WEBSITE_USAGES = new Set(['product-main', 'product-secondary', 'product-third', 'category-hero', 'site-static']);

function isWebsiteMedia(usage: string[]): boolean {
  return usage.some(u => WEBSITE_USAGES.has(u));
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Generate a thumbnail URL for a video using Cloudinary transformations */
function videoThumbnailUrl(cloudinaryUrl: string): string {
  if (!cloudinaryUrl) return '';
  // Transform: /video/upload/... → /video/upload/w_400,h_400,c_fill,so_0,f_jpg/.../
  // so_0 = snapshot at 0 seconds, f_jpg = output as JPEG
  return cloudinaryUrl
    .replace('/video/upload/', '/video/upload/w_400,h_400,c_fill,so_0,f_jpg/')
    .replace(/\.\w+$/, '.jpg');
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function CMSMedia() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  // Core data
  const [media, setMedia] = useState<CmsMediaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterUsage, setFilterUsage] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSpecial, setFilterSpecial] = useState<'all' | 'duplicates' | 'missing-alt' | 'unlinked'>('all');
  const [filterMediaType, setFilterMediaType] = useState<'all' | 'image' | 'video'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name' | 'size'>('newest');

  // View
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Detail panel
  const [detailMedia, setDetailMedia] = useState<CmsMediaRecord | null>(null);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Bulk tag modal
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [bulkTags, setBulkTags] = useState<string[]>([]);

  // Tags list (for autocomplete)
  const [allTags, setAllTags] = useState<string[]>([]);

  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState({ done: 0, failed: 0, total: 0 });

  // Bulk AI alt text generation state
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkGenProgress, setBulkGenProgress] = useState({ done: 0, failed: 0, total: 0 });

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const fetchMedia = useCallback(async () => {
    try {
      if (!token) return;
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterSpecial === 'duplicates') params.set('hasDuplicates', 'true');
      if (filterSpecial === 'missing-alt') params.set('missingAltText', 'true');
      if (filterSpecial === 'unlinked') params.set('unlinked', 'true');
      if (search) params.set('search', search);

      const response = await fetch(`/api/cms/media?${params.toString()}&_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch media');
      const data = await response.json();

      setMedia((data.media || []).map((m: CmsMediaRecord) => ({
        ...m,
        cloudinaryURL: m.cloudinaryUrl,
        tags: m.tags || [],
        fileHash: m.fileHash || null,
        fileSize: m.fileSize || null,
        altTexts: m.altTexts || {},
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch media');
    } finally {
      setLoading(false);
    }
  }, [token, filterSpecial, search]);

  const fetchAllTags = useCallback(async () => {
    try {
      if (!token) return;
      const response = await fetch('/api/cms/media?action=tags', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAllTags(data.tags || []);
      }
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);
  useEffect(() => { fetchAllTags(); }, [fetchAllTags]);

  // Auto-clear success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Lock background scroll when any modal/panel is open
  const isModalOpen = !!detailMedia || showUploadModal || showBulkTagModal;
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isModalOpen]);

  // ─── Computed ─────────────────────────────────────────────────────────────

  const filteredMedia = useMemo(() => {
    let result = [...media];

    if (filterMediaType !== 'all') {
      result = result.filter(m => m.mediaType === filterMediaType);
    }
    if (filterUsage !== 'all') {
      result = result.filter(m => m.usage.includes(filterUsage));
    }
    if (filterCategory !== 'all') {
      result = result.filter(m => m.category === filterCategory);
    }
    if (filterTags.length > 0) {
      result = result.filter(m => filterTags.every(ft => m.tags.includes(ft)));
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
        break;
      case 'name':
        result.sort((a, b) => a.id.localeCompare(b.id));
        break;
      case 'size':
        result.sort((a, b) => (b.fileSize || 0) - (a.fileSize || 0));
        break;
    }

    return result;
  }, [media, filterMediaType, filterUsage, filterCategory, filterTags, sortBy]);

  const categories = useMemo(() =>
    Array.from(new Set(media.map(m => m.category).filter(Boolean))).sort() as string[],
    [media]
  );

  const usageTypes = useMemo(() =>
    Array.from(new Set(media.flatMap(m => m.usage))).sort(),
    [media]
  );

  const stats = useMemo(() => {
    const images = media.filter(m => m.mediaType === 'image').length;
    const videos = media.filter(m => m.mediaType === 'video').length;
    const missingAlt = media.filter(m => {
      const altKeys = Object.keys(m.altTexts || {});
      return altKeys.length < 5 || SUPPORTED_LOCALES.some(l => !m.altTexts?.[l]?.altText);
    }).length;
    const missingHash = media.filter(m => !m.fileHash && m.mediaType !== 'video').length;
    return { total: media.length, images, videos, missingAlt, missingHash };
  }, [media]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  async function patchMedia(body: Record<string, any>) {
    const response = await fetch('/api/cms/media', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'PATCH failed');
    }
    return response.json();
  }

  async function handleUpdateTags(mediaId: string, tags: string[]) {
    try {
      await patchMedia({ action: 'update-tags', mediaId, tags });
      setMedia(prev => prev.map(m => m.id === mediaId ? { ...m, tags } : m));
      if (detailMedia?.id === mediaId) setDetailMedia(prev => prev ? { ...prev, tags } : prev);
      fetchAllTags(); // refresh tag suggestions
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tags');
    }
  }

  async function handleUpdateAltText(mediaId: string, locale: string, altText: string, title: string) {
    try {
      await patchMedia({ action: 'update-alt-text', mediaId, locale, altText, title });
      setMedia(prev => prev.map(m => {
        if (m.id !== mediaId) return m;
        return {
          ...m,
          altTexts: {
            ...m.altTexts,
            [locale]: { altText, title },
          },
        };
      }));
      if (detailMedia?.id === mediaId) {
        setDetailMedia(prev => prev ? {
          ...prev,
          altTexts: { ...prev.altTexts, [locale]: { altText, title } },
        } : prev);
      }
      setSuccess(`Alt text updated (${locale})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update alt text');
    }
  }

  async function handleDeleteMedia(mediaId: string, cloudinaryPublicId: string) {
    if (!confirm(`Delete media "${mediaId}"? This will remove it from Cloudinary and all product links.`)) return;
    try {
      const response = await fetch('/api/cms/media', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mediaId, cloudinaryPublicId }),
      });
      if (!response.ok) throw new Error('Delete failed');
      setMedia(prev => prev.filter(m => m.id !== mediaId));
      if (detailMedia?.id === mediaId) setDetailMedia(null);
      setSuccess('Media deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete media');
    }
  }

  async function handleBulkAddTags() {
    if (bulkTags.length === 0 || selectedIds.size === 0) return;
    try {
      for (const mediaId of selectedIds) {
        const item = media.find(m => m.id === mediaId);
        if (!item) continue;
        const merged = Array.from(new Set([...item.tags, ...bulkTags]));
        await patchMedia({ action: 'update-tags', mediaId, tags: merged });
      }
      await fetchMedia();
      fetchAllTags();
      setSelectedIds(new Set());
      setShowBulkTagModal(false);
      setBulkTags([]);
      setSuccess(`Tags added to ${selectedIds.size} items`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk add tags');
    }
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected media items? This cannot be undone.`)) return;
    try {
      for (const mediaId of selectedIds) {
        const item = media.find(m => m.id === mediaId);
        if (!item?.cloudinaryPublicId) continue;
        await fetch('/api/cms/media', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ mediaId, cloudinaryPublicId: item.cloudinaryPublicId }),
        });
      }
      await fetchMedia();
      setSelectedIds(new Set());
      setSuccess(`Deleted ${selectedIds.size} items`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk delete');
    }
  }

  async function handleBackfillPhash() {
    if (stats.missingHash === 0) return;
    setBackfilling(true);
    setBackfillProgress({ done: 0, failed: 0, total: 0 });

    try {
      // Step 1: Get the list of IDs that need backfilling
      const listResult = await patchMedia({ action: 'backfill-phash-list' });
      const ids: string[] = listResult.ids || [];
      if (ids.length === 0) {
        setSuccess('All media already have phash values');
        setBackfilling(false);
        return;
      }
      setBackfillProgress({ done: 0, failed: 0, total: ids.length });

      // Step 2: Process one at a time with progress updates
      let done = 0;
      let failed = 0;
      for (const mediaId of ids) {
        try {
          const result = await patchMedia({ action: 'backfill-phash', mediaId });
          if (result.success) done++;
          else failed++;
        } catch {
          failed++;
        }
        setBackfillProgress({ done: done + failed, failed, total: ids.length });
      }

      setSuccess(`Backfill complete: ${done} updated, ${failed} failed out of ${ids.length}`);
      await fetchMedia();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  }

  async function handleBulkGenerateAltText() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setBulkGenerating(true);
    setBulkGenProgress({ done: 0, failed: 0, total: ids.length });

    let done = 0;
    let failed = 0;

    for (const mediaId of ids) {
      const m = media.find(item => item.id === mediaId);
      if (!m) { failed++; continue; }

      try {
        const imageUrl = m.mediaType === 'video'
          ? videoThumbnailUrl(m.cloudinaryUrl)
          : m.cloudinaryUrl;

        const res = await fetch('/api/cms/ai-media?action=generate-alt-text-and-save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ mediaId, imageUrl, mediaType: m.mediaType }),
        });

        if (res.ok) done++;
        else failed++;
      } catch {
        failed++;
      }

      setBulkGenProgress({ done: done + failed, failed, total: ids.length });
    }

    setSuccess(`Alt text generated: ${done} succeeded, ${failed} failed out of ${ids.length}`);
    setSelectedIds(new Set());
    await fetchMedia();
    setBulkGenerating(false);
  }

  async function handleGenerateAllMissingAltText() {
    setBulkGenerating(true);
    setBulkGenProgress({ done: 0, failed: 0, total: 0 });

    try {
      // Fetch all media missing alt text (independent of current filter)
      const res = await fetch(`/api/cms/media?missingAltText=true&_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch media');
      const data = await res.json();
      const items: CmsMediaRecord[] = data.media || [];

      if (items.length === 0) {
        setSuccess('All media already has alt text!');
        setBulkGenerating(false);
        return;
      }

      setBulkGenProgress({ done: 0, failed: 0, total: items.length });

      let done = 0;
      let failed = 0;

      for (const m of items) {
        try {
          const imageUrl = m.mediaType === 'video'
            ? videoThumbnailUrl(m.cloudinaryUrl)
            : m.cloudinaryUrl;

          const aiRes = await fetch('/api/cms/ai-media?action=generate-alt-text-and-save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ mediaId: m.id, imageUrl, mediaType: m.mediaType }),
          });

          if (aiRes.ok) done++;
          else failed++;
        } catch {
          failed++;
        }

        setBulkGenProgress({ done: done + failed, failed, total: items.length });
      }

      setSuccess(`Alt text generated: ${done} succeeded, ${failed} failed out of ${items.length}`);
      await fetchMedia();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate alt text');
    } finally {
      setBulkGenerating(false);
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === filteredMedia.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMedia.map(m => m.id)));
    }
  }

  const clearFilters = () => {
    setSearch('');
    setFilterTags([]);
    setFilterMediaType('all');
    setFilterUsage('all');
    setFilterCategory('all');
    setFilterSpecial('all');
    setSortBy('newest');
  };

  const hasActiveFilters = search || filterTags.length > 0 || filterMediaType !== 'all' || filterUsage !== 'all' || filterCategory !== 'all' || filterSpecial !== 'all';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-brand-sand dark:bg-brand-dark">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm dark:bg-brand-charcoal/80 dark:shadow-none dark:border-b dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/cms/dashboard')}
                className="text-sm text-brand-gray hover:text-brand-blue dark:text-white/60 dark:hover:text-brand-blue flex items-center gap-2 transition-colors"
              >
                &larr; Dashboard
              </button>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-dark dark:text-white">Media Library</h1>
            <p className="text-brand-gray dark:text-white/60 mt-1">Manage media assets, tags, and alt text</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {(stats.missingAlt > 0 || bulkGenerating) && (
              <div className="flex items-center gap-3">
                {bulkGenerating && bulkGenProgress.total > 0 && (
                  <div className="flex items-center gap-2 text-sm text-brand-gray dark:text-white/60">
                    <div className="w-32 h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.round((bulkGenProgress.done / bulkGenProgress.total) * 100)}%` }}
                      />
                    </div>
                    <span className="tabular-nums whitespace-nowrap">
                      {bulkGenProgress.done}/{bulkGenProgress.total}
                      {bulkGenProgress.failed > 0 && <span className="text-red-500"> ({bulkGenProgress.failed} failed)</span>}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleGenerateAllMissingAltText}
                  disabled={bulkGenerating}
                  className="px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 disabled:opacity-50 transition-colors"
                >
                  {bulkGenerating
                    ? bulkGenProgress.total > 0
                      ? 'Generating...'
                      : 'Loading...'
                    : `Generate All Alt Text (${stats.missingAlt})`
                  }
                </button>
              </div>
            )}
            {(stats.missingHash > 0 || backfilling) && (
              <div className="flex items-center gap-3">
                {backfilling && backfillProgress.total > 0 && (
                  <div className="flex items-center gap-2 text-sm text-brand-gray dark:text-white/60">
                    <div className="w-32 h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-blue rounded-full transition-all duration-300"
                        style={{ width: `${Math.round((backfillProgress.done / backfillProgress.total) * 100)}%` }}
                      />
                    </div>
                    <span className="tabular-nums whitespace-nowrap">
                      {backfillProgress.done}/{backfillProgress.total}
                      {backfillProgress.failed > 0 && <span className="text-red-500"> ({backfillProgress.failed} failed)</span>}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleBackfillPhash}
                  disabled={backfilling}
                  className="px-4 py-2 text-sm border border-black/15 dark:border-white/15 rounded-lg text-brand-dark dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  {backfilling
                    ? backfillProgress.total > 0
                      ? `Processing...`
                      : 'Loading...'
                    : `Backfill phash (${stats.missingHash})`
                  }
                </button>
              </div>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-5 py-2 bg-brand-blue hover:bg-brand-dark text-white rounded-lg font-semibold transition-colors"
            >
              Upload Media
            </button>
            <button
              onClick={() => { logout(); navigate('/cms/login'); }}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition"
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Status Messages */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-950/50 border border-red-300 dark:border-red-900 text-red-800 dark:text-red-200">
            {error}
            <button onClick={() => setError(null)} className="ml-2 font-bold">&times;</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 rounded-lg bg-green-100 dark:bg-green-950/50 border border-green-300 dark:border-green-900 text-green-800 dark:text-green-200">
            {success}
          </div>
        )}

        {/* Stats */}
        <div className="flex flex-wrap gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-brand-dark dark:text-white' },
            { label: 'Images', value: stats.images, color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Videos', value: stats.videos, color: 'text-purple-600 dark:text-purple-400' },
            { label: 'Missing Alt Text', value: stats.missingAlt, color: stats.missingAlt > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400' },
          ].map(stat => (
            <div key={stat.label} className="px-4 py-2 bg-white/80 dark:bg-brand-charcoal/60 rounded-lg border border-black/5 dark:border-white/10">
              <span className="text-sm text-brand-gray dark:text-white/60">{stat.label}: </span>
              <span className={`font-bold ${stat.color}`}>{stat.value}</span>
            </div>
          ))}
          {/* Website vs Marketing legend */}
          <div className="flex items-center gap-3 px-4 py-2 bg-white/80 dark:bg-brand-charcoal/60 rounded-lg border border-black/5 dark:border-white/10">
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
              <span className="text-brand-gray dark:text-white/60">Website</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" />
              <span className="text-brand-gray dark:text-white/60">Marketing</span>
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-start">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search ID, tags, category..."
            className="px-4 py-2.5 border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-charcoal/80 text-brand-dark dark:text-white placeholder-brand-gray/50 min-w-0 md:min-w-[200px] w-full md:w-auto focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
          />

          <select
            value={filterMediaType}
            onChange={e => setFilterMediaType(e.target.value as any)}
            className="px-3 py-2 border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-charcoal/80 text-brand-dark dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>

          <div className="min-w-0 md:min-w-[180px] w-full md:w-auto">
            <TagInput
              tags={filterTags}
              allTags={allTags}
              onChange={setFilterTags}
              placeholder="Filter by tags..."
            />
          </div>

          <select
            value={filterUsage}
            onChange={e => setFilterUsage(e.target.value)}
            className="px-3 py-2 border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-charcoal/80 text-brand-dark dark:text-white"
          >
            <option value="all">All Usage</option>
            {usageTypes.map(u => <option key={u} value={u}>{u}</option>)}
          </select>

          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-charcoal/80 text-brand-dark dark:text-white"
          >
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            value={filterSpecial}
            onChange={e => setFilterSpecial(e.target.value as any)}
            className="px-3 py-2 border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-charcoal/80 text-brand-dark dark:text-white"
          >
            <option value="all">All Media</option>
            <option value="duplicates">Duplicates Only</option>
            <option value="missing-alt">Missing Alt Text</option>
            <option value="unlinked">No product link</option>
          </select>

          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as any)}
            className="px-3 py-2 border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-charcoal/80 text-brand-dark dark:text-white"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name">Name</option>
            <option value="size">Size</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-brand-gray hover:text-brand-dark dark:hover:text-white transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* View toggle + Bulk actions + Select all */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={toggleSelectAll} className="text-sm text-brand-gray hover:text-brand-dark dark:hover:text-white">
              {selectedIds.size === filteredMedia.length && filteredMedia.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
            {selectedIds.size > 0 && (
              <span className="text-sm text-brand-blue font-medium">{selectedIds.size} selected</span>
            )}
            {selectedIds.size > 0 && (
              <>
                <button
                  onClick={() => setShowBulkTagModal(true)}
                  className="px-3 py-1.5 text-sm bg-brand-blue/10 text-brand-blue rounded-lg hover:bg-brand-blue/20 transition-colors"
                >
                  Add Tags
                </button>
                <button
                  onClick={handleBulkGenerateAltText}
                  disabled={bulkGenerating}
                  className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 disabled:opacity-50 transition-colors"
                >
                  {bulkGenerating ? `Generating ${bulkGenProgress.done}/${bulkGenProgress.total}...` : 'Generate Alt Text'}
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 transition-colors"
                >
                  Delete Selected
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 bg-white/80 dark:bg-brand-charcoal/60 rounded-lg border border-black/10 dark:border-white/10 p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${viewMode === 'grid' ? 'bg-brand-blue text-white' : 'text-brand-gray hover:text-brand-dark dark:hover:text-white'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${viewMode === 'list' ? 'bg-brand-blue text-white' : 'text-brand-gray hover:text-brand-dark dark:hover:text-white'}`}
            >
              List
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-brand-gray dark:text-white/50">
            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading media...
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="text-center py-20 text-brand-gray dark:text-white/50">
            {hasActiveFilters ? 'No media matches your filters.' : 'No media found.'}
          </div>
        ) : viewMode === 'grid' ? (
          <MediaGrid
            media={filteredMedia}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onClickMedia={setDetailMedia}
          />
        ) : (
          <MediaList
            media={filteredMedia}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onClickMedia={setDetailMedia}
          />
        )}

        <div className="mt-4 text-sm text-brand-gray dark:text-white/50">
          Showing {filteredMedia.length} of {media.length} media items
        </div>
      </main>

      {/* Detail Panel */}
      {detailMedia && (
        <MediaDetailPanel
          media={detailMedia}
          allTags={allTags}
          allMedia={media}
          onClose={() => setDetailMedia(null)}
          onUpdateTags={handleUpdateTags}
          onUpdateAltText={handleUpdateAltText}
          onDelete={handleDeleteMedia}
          token={token!}
          onPopulateAltTexts={(mediaId, altTexts) => {
            // Update local media state with AI-generated alt texts for preview
            setMedia(prev => prev.map(m =>
              m.id === mediaId ? { ...m, altTexts: { ...m.altTexts, ...altTexts } } : m
            ));
            if (detailMedia?.id === mediaId) {
              setDetailMedia(prev => prev ? { ...prev, altTexts: { ...prev.altTexts, ...altTexts } } : prev);
            }
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <BulkUploadModal
          token={token!}
          allTags={allTags}
          onClose={() => setShowUploadModal(false)}
          onComplete={() => {
            setShowUploadModal(false);
            fetchMedia();
            fetchAllTags();
            setSuccess('Upload complete');
          }}
        />
      )}

      {/* Bulk Tag Modal */}
      {showBulkTagModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-brand-charcoal rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-brand-dark dark:text-white mb-4">
              Add Tags to {selectedIds.size} Items
            </h3>
            <TagInput tags={bulkTags} allTags={allTags} onChange={setBulkTags} placeholder="Tags to add..." />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowBulkTagModal(false); setBulkTags([]); }}
                className="px-4 py-2 text-sm border border-black/15 dark:border-white/15 rounded-lg text-brand-dark dark:text-white/80"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddTags}
                disabled={bulkTags.length === 0}
                className="px-4 py-2 text-sm bg-brand-blue hover:bg-brand-dark text-white rounded-lg font-semibold disabled:opacity-50"
              >
                Add Tags
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Grid View ──────────────────────────────────────────────────────────────

function MediaGrid({
  media, selectedIds, onToggleSelect, onClickMedia,
}: {
  media: CmsMediaRecord[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onClickMedia: (m: CmsMediaRecord) => void;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {media.map(m => (
        <div
          key={m.id}
          className={`relative group bg-white/80 dark:bg-brand-charcoal/60 rounded-xl border overflow-hidden transition-all cursor-pointer hover:shadow-md ${
            selectedIds.has(m.id) ? 'border-brand-blue ring-2 ring-brand-blue/30' : 'border-black/5 dark:border-white/10'
          }`}
        >
          {/* Checkbox */}
          <label
            className="absolute top-2 left-2 z-10"
            onClick={e => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={selectedIds.has(m.id)}
              onChange={() => onToggleSelect(m.id)}
              className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
            />
          </label>

          {/* Thumbnail */}
          <div className="aspect-square bg-gray-100 dark:bg-brand-dark/50 relative" onClick={() => onClickMedia(m)}>
            {m.mediaType === 'video' ? (
              <div className="w-full h-full relative">
                <img
                  src={videoThumbnailUrl(m.cloudinaryUrl)}
                  alt={m.id}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white text-lg">&#9654;</span>
                </div>
              </div>
            ) : (
              <img
                src={m.cloudinaryUrl}
                alt={m.id}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
            {/* Website vs Marketing indicator */}
            {isWebsiteMedia(m.usage) ? (
              <span
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-600/90 flex items-center justify-center shadow-sm"
                title="Used on website"
              >
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </span>
            ) : (
              <span
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-amber-500/90 flex items-center justify-center shadow-sm"
                title="Marketing / not on website"
              >
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.9 47.9 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0z" />
                </svg>
              </span>
            )}
          </div>

          {/* Info */}
          <div className="p-3 space-y-2" onClick={() => onClickMedia(m)}>
            <p className="text-xs font-mono text-brand-dark dark:text-white/80 truncate" title={m.id}>
              {m.id}
            </p>

            {/* Tags */}
            {m.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {m.tags.slice(0, 3).map(t => (
                  <span key={t} className="px-1.5 py-0.5 text-[10px] rounded bg-brand-blue/10 text-brand-blue border border-brand-blue/20">
                    {t}
                  </span>
                ))}
                {m.tags.length > 3 && (
                  <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 dark:bg-white/10 text-brand-gray">
                    +{m.tags.length - 3}
                  </span>
                )}
              </div>
            )}

            {/* Usage badges */}
            <div className="flex flex-wrap gap-1">
              {m.usage.map(u => (
                <span key={u} className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${usageBadgeClass(u)}`}>
                  {u}
                </span>
              ))}
            </div>

            {/* Alt text status + file size */}
            <div className="flex items-center justify-between">
              <div className="flex gap-0.5" title="Alt text status (EN IT ES FR DE)">
                {SUPPORTED_LOCALES.map(l => (
                  <span
                    key={l}
                    className={`w-2 h-2 rounded-full ${
                      m.altTexts?.[l]?.altText ? 'bg-green-500' : 'bg-gray-300 dark:bg-white/20'
                    }`}
                    title={`${LOCALE_LABELS[l]}: ${m.altTexts?.[l]?.altText ? 'Has alt text' : 'Missing'}`}
                  />
                ))}
              </div>
              {m.fileSize && (
                <span className="text-[10px] text-brand-gray">{formatFileSize(m.fileSize)}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── List View ──────────────────────────────────────────────────────────────

function MediaList({
  media, selectedIds, onToggleSelect, onClickMedia,
}: {
  media: CmsMediaRecord[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onClickMedia: (m: CmsMediaRecord) => void;
}) {
  return (
    <div className="bg-white/80 dark:bg-brand-charcoal/60 rounded-xl border border-black/5 dark:border-white/10 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 dark:border-white/10 text-left">
              <th className="p-3 w-10"></th>
              <th className="p-3 w-16">Preview</th>
              <th className="p-3">ID</th>
              <th className="p-3">Type</th>
              <th className="p-3">Tags</th>
              <th className="p-3">Usage</th>
              <th className="p-3">Category</th>
              <th className="p-3">Products</th>
              <th className="p-3">Alt Text</th>
              <th className="p-3">Size</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5 dark:divide-white/5">
            {media.map(m => (
              <tr
                key={m.id}
                className="hover:bg-black/[0.02] dark:hover:bg-white/[0.03] cursor-pointer"
                onClick={() => onClickMedia(m)}
              >
                <td className="p-3" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(m.id)}
                    onChange={() => onToggleSelect(m.id)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                  />
                </td>
                <td className="p-3">
                  <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-brand-dark/50 flex-shrink-0 relative">
                    {m.mediaType === 'image' ? (
                      <img src={m.cloudinaryUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="relative w-full h-full">
                        <img src={videoThumbnailUrl(m.cloudinaryUrl)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white text-xs drop-shadow">&#9654;</span>
                        </div>
                      </div>
                    )}
                    {/* Website vs Marketing dot */}
                    <span
                      className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border border-white dark:border-brand-charcoal ${
                        isWebsiteMedia(m.usage) ? 'bg-green-500' : 'bg-amber-500'
                      }`}
                      title={isWebsiteMedia(m.usage) ? 'Used on website' : 'Marketing / not on website'}
                    />
                  </div>
                </td>
                <td className="p-3 font-mono text-xs max-w-[200px] truncate" title={m.id}>{m.id}</td>
                <td className="p-3 text-xs">{m.mediaType}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {m.tags.slice(0, 2).map(t => (
                      <span key={t} className="px-1.5 py-0.5 text-[10px] rounded bg-brand-blue/10 text-brand-blue">{t}</span>
                    ))}
                    {m.tags.length > 2 && <span className="text-[10px] text-brand-gray">+{m.tags.length - 2}</span>}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {m.usage.map(u => (
                      <span key={u} className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${usageBadgeClass(u)}`}>{u}</span>
                    ))}
                  </div>
                </td>
                <td className="p-3 text-xs text-brand-gray">{m.category || '-'}</td>
                <td className="p-3 text-xs">{m.products.length}</td>
                <td className="p-3">
                  <div className="flex gap-0.5">
                    {SUPPORTED_LOCALES.map(l => (
                      <span
                        key={l}
                        className={`w-2 h-2 rounded-full ${m.altTexts?.[l]?.altText ? 'bg-green-500' : 'bg-gray-300 dark:bg-white/20'}`}
                        title={`${LOCALE_LABELS[l]}: ${m.altTexts?.[l]?.altText ? 'Has alt text' : 'Missing'}`}
                      />
                    ))}
                  </div>
                </td>
                <td className="p-3 text-xs text-brand-gray">{formatFileSize(m.fileSize)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Detail Panel ───────────────────────────────────────────────────────────

function MediaDetailPanel({
  media, allTags, allMedia, onClose, onUpdateTags, onUpdateAltText, onDelete, token, onPopulateAltTexts,
}: {
  media: CmsMediaRecord;
  allTags: string[];
  allMedia: CmsMediaRecord[];
  onClose: () => void;
  onUpdateTags: (id: string, tags: string[]) => void;
  onUpdateAltText: (id: string, locale: string, altText: string, title: string) => void;
  onDelete: (id: string, publicId: string) => void;
  token: string;
  onPopulateAltTexts: (mediaId: string, altTexts: Record<string, { altText: string; title: string }>) => void;
}) {
  const [activeLocale, setActiveLocale] = useState<string>('en');
  const [altText, setAltText] = useState('');
  const [altTitle, setAltTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  // Local alt text state for all locales (populated by AI or manual edits)
  const [localAltTexts, setLocalAltTexts] = useState<Record<string, { altText: string; title: string }>>({});

  // Sync alt text fields when locale or media changes
  useEffect(() => {
    const local = localAltTexts[activeLocale];
    const existing = media.altTexts?.[activeLocale];
    setAltText(local?.altText ?? existing?.altText ?? '');
    setAltTitle(local?.title ?? existing?.title ?? '');
  }, [activeLocale, media.id, media.altTexts, localAltTexts]);

  // Reset local state when media changes
  useEffect(() => {
    setLocalAltTexts({});
  }, [media.id]);

  // Track manual edits in local state
  function handleAltTextChange(value: string) {
    setAltText(value);
    setLocalAltTexts(prev => ({
      ...prev,
      [activeLocale]: { altText: value, title: prev[activeLocale]?.title ?? altTitle },
    }));
  }

  function handleAltTitleChange(value: string) {
    setAltTitle(value);
    setLocalAltTexts(prev => ({
      ...prev,
      [activeLocale]: { altText: prev[activeLocale]?.altText ?? altText, title: value },
    }));
  }

  // Find duplicates by file_hash
  const duplicates = media.fileHash
    ? allMedia.filter(m => m.id !== media.id && m.fileHash === media.fileHash)
    : [];

  async function saveAltText() {
    setSaving(true);
    await onUpdateAltText(media.id, activeLocale, altText, altTitle);
    setSaving(false);
  }

  async function saveAllLocales() {
    setSavingAll(true);
    for (const locale of SUPPORTED_LOCALES) {
      const local = localAltTexts[locale];
      const existing = media.altTexts?.[locale];
      const at = local?.altText ?? existing?.altText ?? '';
      const ti = local?.title ?? existing?.title ?? '';
      if (at || ti) {
        await onUpdateAltText(media.id, locale, at, ti);
      }
    }
    setSavingAll(false);
  }

  async function handleGenerateAltText() {
    setGenerating(true);
    try {
      const imageUrl = media.mediaType === 'video'
        ? videoThumbnailUrl(media.cloudinaryUrl)
        : media.cloudinaryUrl;

      const res = await fetch('/api/cms/ai-media?action=generate-alt-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mediaId: media.id, imageUrl, mediaType: media.mediaType }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to generate alt text');
      }

      const data = await res.json();
      if (data.altTexts) {
        setLocalAltTexts(data.altTexts);
        onPopulateAltTexts(media.id, data.altTexts);
      }
    } catch (err) {
      console.error('[CMSMedia] AI alt text generation failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to generate alt text');
    } finally {
      setGenerating(false);
    }
  }

  // Check if there are unsaved AI-generated alt texts
  const hasUnsavedAiTexts = Object.keys(localAltTexts).length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-white dark:bg-brand-charcoal h-full overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-brand-charcoal border-b border-black/10 dark:border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-brand-dark dark:text-white truncate">{media.id}</h2>
          <button onClick={onClose} className="text-2xl text-brand-gray hover:text-brand-dark dark:hover:text-white">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Preview */}
          <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-brand-dark/50">
            {media.mediaType === 'video' ? (
              <video src={media.cloudinaryUrl} controls className="w-full max-h-80 object-contain" />
            ) : (
              <img src={media.cloudinaryUrl} alt={media.id} className="w-full max-h-80 object-contain" />
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-brand-gray dark:text-white/50">Type:</span>{' '}
              <span className="text-brand-dark dark:text-white">{media.mediaType}</span>
            </div>
            <div>
              <span className="text-brand-gray dark:text-white/50">Format:</span>{' '}
              <span className="text-brand-dark dark:text-white">{media.format || '-'}</span>
            </div>
            {media.width && media.height && (
              <div>
                <span className="text-brand-gray dark:text-white/50">Dimensions:</span>{' '}
                <span className="text-brand-dark dark:text-white">{media.width} x {media.height}</span>
              </div>
            )}
            <div>
              <span className="text-brand-gray dark:text-white/50">Size:</span>{' '}
              <span className="text-brand-dark dark:text-white">{formatFileSize(media.fileSize) || '-'}</span>
            </div>
            <div className="col-span-2">
              <span className="text-brand-gray dark:text-white/50">Cloudinary ID:</span>{' '}
              <span className="text-brand-dark dark:text-white font-mono text-xs break-all">{media.cloudinaryPublicId || '-'}</span>
            </div>
            {media.fileHash && (
              <div className="col-span-2">
                <span className="text-brand-gray dark:text-white/50">phash:</span>{' '}
                <span className="text-brand-dark dark:text-white font-mono text-xs">{media.fileHash}</span>
              </div>
            )}
          </div>

          {/* Usage */}
          <div>
            <h3 className="text-sm font-semibold text-brand-dark dark:text-white mb-2">Usage</h3>
            <div className="flex flex-wrap gap-2">
              {media.usage.map(u => (
                <span key={u} className={`px-2 py-1 text-xs rounded font-medium ${usageBadgeClass(u)}`}>{u}</span>
              ))}
              {media.usage.length === 0 && <span className="text-sm text-brand-gray">No usage assigned</span>}
            </div>
          </div>

          {/* Linked Products */}
          <div>
            <h3 className="text-sm font-semibold text-brand-dark dark:text-white mb-2">
              Linked Products ({media.products.length})
            </h3>
            {media.products.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {media.products.map(p => (
                  <span key={p} className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-white/10 text-brand-dark dark:text-white/80 font-mono">
                    {p}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-brand-gray">Not linked to any products</span>
            )}
          </div>

          {/* Tags */}
          <div>
            <h3 className="text-sm font-semibold text-brand-dark dark:text-white mb-2">Tags</h3>
            <TagInput
              tags={media.tags}
              allTags={allTags}
              onChange={tags => onUpdateTags(media.id, tags)}
            />
          </div>

          {/* Alt Text */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-brand-dark dark:text-white">Alt Text (SEO)</h3>
              <button
                onClick={handleGenerateAltText}
                disabled={generating}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 disabled:opacity-50 transition-colors"
              >
                {generating ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    Generate with AI
                  </>
                )}
              </button>
            </div>

            {/* Locale tabs */}
            <div className="flex border-b border-black/10 dark:border-white/10 mb-3">
              {SUPPORTED_LOCALES.map(l => (
                <button
                  key={l}
                  onClick={() => setActiveLocale(l)}
                  className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                    activeLocale === l
                      ? 'text-brand-blue'
                      : 'text-brand-gray hover:text-brand-dark dark:hover:text-white'
                  }`}
                >
                  {LOCALE_LABELS[l]}
                  {media.altTexts?.[l]?.altText && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-500" />
                  )}
                  {activeLocale === l && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-blue" />
                  )}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-brand-gray dark:text-white/50 mb-1">Alt Text</label>
                <textarea
                  value={altText}
                  onChange={e => handleAltTextChange(e.target.value)}
                  rows={2}
                  placeholder={`Describe this image in ${LOCALE_LABELS[activeLocale]}...`}
                  className="w-full px-3 py-2 text-sm border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-dark/50 text-brand-dark dark:text-white placeholder-brand-gray/50 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
              </div>
              <div>
                <label className="block text-xs text-brand-gray dark:text-white/50 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={altTitle}
                  onChange={e => handleAltTitleChange(e.target.value)}
                  placeholder={`Image title in ${LOCALE_LABELS[activeLocale]}...`}
                  className="w-full px-3 py-2 text-sm border border-black/15 dark:border-white/15 rounded-lg bg-white dark:bg-brand-dark/50 text-brand-dark dark:text-white placeholder-brand-gray/50 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={saveAltText}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-brand-blue hover:bg-brand-dark text-white rounded-lg font-semibold disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : `Save ${LOCALE_LABELS[activeLocale]}`}
                </button>
                {hasUnsavedAiTexts && (
                  <button
                    onClick={saveAllLocales}
                    disabled={savingAll}
                    className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-50 transition-colors"
                  >
                    {savingAll ? 'Saving...' : 'Save All Locales'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Duplicates */}
          {duplicates.length > 0 && (() => {
            // Build comparison list: current media + all duplicates
            const allCopies = [media, ...duplicates];
            // Score each: website usage > product links > has alt text
            const scored = allCopies.map(m => {
              const onWebsite = isWebsiteMedia(m.usage);
              const productCount = m.products.length;
              const hasAlt = SUPPORTED_LOCALES.some(l => m.altTexts?.[l]?.altText);
              const score = (onWebsite ? 100 : 0) + productCount * 10 + (hasAlt ? 1 : 0);
              return { media: m, onWebsite, productCount, hasAlt, score };
            });
            scored.sort((a, b) => b.score - a.score);
            const recommendedId = scored[0].media.id;

            return (
              <div>
                <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">
                  Duplicates Found ({allCopies.length} copies)
                </h3>
                <p className="text-xs text-brand-gray dark:text-white/50 mb-3">
                  Same image detected. Keep the one in use and delete the rest.
                </p>
                <div className="space-y-2">
                  {scored.map(({ media: m, onWebsite, productCount, hasAlt }) => {
                    const isRecommended = m.id === recommendedId;
                    const isCurrent = m.id === media.id;
                    return (
                      <div
                        key={m.id}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                          isRecommended
                            ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30'
                            : 'border-black/10 bg-gray-50 dark:border-white/10 dark:bg-brand-dark/30'
                        }`}
                      >
                        {/* Thumbnail */}
                        <div className="w-12 h-12 rounded overflow-hidden bg-gray-100 dark:bg-brand-dark/50 flex-shrink-0">
                          <img src={m.cloudinaryUrl} alt="" className="w-full h-full object-cover" />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-mono text-brand-dark dark:text-white/80 truncate" title={m.id}>
                              {m.id}
                            </span>
                            {isCurrent && (
                              <span className="px-1.5 py-0.5 text-[9px] rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium flex-shrink-0">
                                VIEWING
                              </span>
                            )}
                            {isRecommended && (
                              <span className="px-1.5 py-0.5 text-[9px] rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium flex-shrink-0">
                                KEEP
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-brand-gray dark:text-white/50">
                            {onWebsite ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">On website</span>
                            ) : (
                              <span>Not on website</span>
                            )}
                            <span>{productCount} product{productCount !== 1 ? 's' : ''}</span>
                            {hasAlt && <span>Has alt text</span>}
                          </div>
                          {m.usage.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {m.usage.map(u => (
                                <span key={u} className={`px-1 py-0.5 text-[9px] rounded font-medium ${usageBadgeClass(u)}`}>{u}</span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Delete button (only for non-recommended) */}
                        {!isRecommended && !isCurrent && (
                          <button
                            onClick={() => onDelete(m.id, m.cloudinaryPublicId || '')}
                            className="px-2.5 py-1.5 text-[10px] bg-red-100 text-red-700 rounded-lg hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 font-medium flex-shrink-0 transition-colors"
                          >
                            Delete
                          </button>
                        )}
                        {!isRecommended && isCurrent && (
                          <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium flex-shrink-0">
                            Safe to delete
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Delete */}
          <div className="pt-4 border-t border-black/10 dark:border-white/10">
            <button
              onClick={() => onDelete(media.id, media.cloudinaryPublicId || '')}
              className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
            >
              Delete Media
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Upload Modal ──────────────────────────────────────────────────────

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  preview?: string;
}

function BulkUploadModal({
  token, allTags, onClose, onComplete,
}: {
  token: string;
  allTags: string[];
  onClose: () => void;
  onComplete: () => void;
}) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'video/mp4', 'video/webm', 'video/quicktime'];
  const maxImageSize = 10 * 1024 * 1024;
  const maxVideoSize = 100 * 1024 * 1024;

  function addFiles(fileList: FileList | File[]) {
    const newFiles: UploadFile[] = Array.from(fileList).map(file => {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? maxVideoSize : maxImageSize;
      let error: string | undefined;
      if (!validTypes.includes(file.type)) error = 'Unsupported type';
      if (file.size > maxSize) error = `Too large (max ${isVideo ? '100MB' : '10MB'})`;

      return {
        file,
        id: `${file.name}-${Date.now()}`,
        status: error ? 'error' as const : 'pending' as const,
        error,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      };
    });
    setFiles(prev => [...prev, ...newFiles]);
  }

  function removeFile(id: string) {
    setFiles(prev => {
      const removed = prev.find(f => f.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter(f => f.id !== id);
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }

  async function handleUpload() {
    setUploading(true);
    const pendingFiles = files.filter(f => f.status === 'pending');

    for (const uf of pendingFiles) {
      setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, status: 'uploading' } : f));

      try {
        const formData = new FormData();
        formData.append('file', uf.file);
        formData.append('mediaType', uf.file.type.startsWith('video/') ? 'video' : 'image');
        // Generate a media ID from the filename
        const sanitizedName = uf.file.name
          .replace(/\.[^.]+$/, '') // remove extension
          .replace(/[^a-zA-Z0-9-_]/g, '-') // sanitize
          .replace(/-+/g, '-') // collapse dashes
          .toLowerCase();
        formData.append('mediaId', `${sanitizedName}-${Date.now().toString(36)}`);
        if (uploadTags.length > 0) formData.append('tags', JSON.stringify(uploadTags));

        const response = await fetch('/api/cms/media', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Upload failed');
        }

        setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, status: 'done' } : f));
      } catch (err) {
        setFiles(prev => prev.map(f => f.id === uf.id ? {
          ...f,
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        } : f));
      }
    }

    setUploading(false);
    // Check updated state for completion (files state is stale in this closure)
    setFiles(prev => {
      const allDone = prev.every(f => f.status === 'done' || f.status === 'error');
      if (allDone) setTimeout(onComplete, 500);
      return prev;
    });
  }

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const doneCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-brand-charcoal rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-bold text-brand-dark dark:text-white">Bulk Upload</h2>
          <button onClick={onClose} className="text-2xl text-brand-gray hover:text-brand-dark dark:hover:text-white">&times;</button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-black/20 dark:border-white/20 rounded-xl p-8 text-center hover:border-brand-blue/50 transition-colors"
          >
            <p className="text-brand-gray dark:text-white/60 mb-2">Drag &amp; drop files here, or</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-brand-blue hover:bg-brand-dark text-white rounded-lg font-semibold transition-colors"
            >
              Choose Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
              onChange={e => { if (e.target.files) addFiles(e.target.files); }}
              className="hidden"
            />
            <p className="text-xs text-brand-gray dark:text-white/40 mt-2">
              JPEG, PNG, WebP, HEIC (10MB max) | MP4, WebM, MOV (100MB max)
            </p>
          </div>

          {/* Tags for upload */}
          <div>
            <label className="block text-sm font-semibold text-brand-dark dark:text-white mb-1">Tags (applied to all uploads)</label>
            <TagInput tags={uploadTags} allTags={allTags} onChange={setUploadTags} placeholder="Add tags..." />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-brand-dark dark:text-white">
                Files ({files.length})
              </h3>
              {files.map(f => (
                <div key={f.id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-brand-dark/30">
                  {/* Preview */}
                  <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 dark:bg-brand-dark/50 flex-shrink-0">
                    {f.preview ? (
                      <img src={f.preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-brand-gray/40 text-lg">&#9654;</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-brand-dark dark:text-white truncate">{f.file.name}</p>
                    <p className="text-xs text-brand-gray">{formatFileSize(f.file.size)}</p>
                  </div>
                  {/* Status */}
                  {f.status === 'pending' && (
                    <button onClick={() => removeFile(f.id)} className="text-sm text-brand-gray hover:text-red-500">&times;</button>
                  )}
                  {f.status === 'uploading' && (
                    <svg className="animate-spin h-4 w-4 text-brand-blue" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {f.status === 'done' && <span className="text-green-600 text-sm font-medium">Done</span>}
                  {f.status === 'error' && (
                    <span className="text-red-600 text-xs" title={f.error}>{f.error || 'Failed'}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Progress summary */}
          {uploading && (
            <div className="text-sm text-brand-gray dark:text-white/60">
              Uploading... {doneCount}/{files.length} complete {errorCount > 0 && `(${errorCount} failed)`}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex items-center justify-between">
          <span className="text-sm text-brand-gray">
            {pendingCount > 0 ? `${pendingCount} files ready` : doneCount > 0 ? `${doneCount} uploaded` : 'No files selected'}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-black/15 dark:border-white/15 rounded-lg text-brand-dark dark:text-white/80"
            >
              {doneCount > 0 ? 'Done' : 'Cancel'}
            </button>
            {pendingCount > 0 && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-5 py-2 text-sm bg-brand-blue hover:bg-brand-dark text-white rounded-lg font-semibold disabled:opacity-50 transition-colors"
              >
                {uploading ? 'Uploading...' : `Upload ${pendingCount} File${pendingCount > 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
